import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '../contexts/StoreContext';
import { Gallery } from '../components/vehicle/Gallery';
import { AvailabilityBadge } from '../components/AvailabilityBadge';
import { OfferStatus } from '../components/OfferStatus';
import { FinanceConditions } from '../components/FinanceConditions';
import { CircularTable } from '../components/simulator/CircularTable';
import { eligibleLines } from '../utils/circular';
import { InquiryForm } from '../components/InquiryForm';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { getQuickQuote } from '../utils/finance';
import { formatARS, formatDate, formatPct } from '../utils/format';
function Spec({
  label,
  value
}: {
  label: string;
  value: string | null;
}) {
  return <div className="py-2.5 sm:grid sm:grid-cols-[180px_1fr] sm:gap-4" data-id="element-1450" data-mp="2hqhu02yvs198">
      <dt className="text-sm text-mist" data-id="element-1451" data-mp="2lfes9gl8qsot">{label}</dt>
      <dd className={`text-sm ${value ? 'text-white' : 'italic text-mist/70'}`} data-id="element-1452" data-mp="2le4rgjn076eh">{value ?? 'No disponible'}</dd>
    </div>;
}
function ListBlock({
  items
}: {
  items: string[];
}) {
  if (!items.length) return <p className="text-sm italic text-mist/70" data-id="element-1453" data-mp="2vdcclszzc5c5">No disponible</p>;
  return <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1454" data-mp="2qgc83mis3an5">
      {items.map(i => <li key={i} className="flex items-start gap-2 text-sm" data-id="element-1455" data-mp="22ooprszl17wh">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-neon" aria-hidden data-id="element-1456" data-mp="22n3x68wtvz19" />
          {i}
        </li>)}
    </ul>;
}
export function VehicleDetail() {
  const {
    id
  } = useParams();
  const {
    vehicles,
    brands,
    compareIds,
    toggleCompare,
    catalogSync
  } = useStore();
  const vehicle = vehicles.find(v => v.id === id && v.published);
  const quote = useMemo(() => vehicle ? getQuickQuote(vehicle, brands) : null, [vehicle, brands]);
  if (!vehicle && catalogSync.status === 'loading') {
    return <div className="container-x py-24 text-center" role="status" data-id="element-1457" data-mp="3f2dagubfdbnk">
        <p className="font-display text-xl font-bold" data-id="element-1458" data-mp="3f00xkvkixrhd">Cargando unidad…</p>
        <p className="mt-2 text-sm text-mist" data-id="element-1459" data-mp="3jznaf3wfx5pn">Estamos consultando el inventario actualizado.</p>
      </div>;
  }
  if (!vehicle) {
    return <div className="container-x py-24 text-center" data-id="element-1460" data-mp="3sta50qoww2x9">
        <h1 className="font-display text-2xl font-bold" data-id="element-1461" data-mp="3st08sld9wzj4">Unidad no disponible</h1>
        <p className="mt-2 text-mist" data-id="element-1462" data-mp="2w4klkvxmz296">Puede que haya sido dada de baja o que el enlace sea incorrecto.</p>
        <Link to="/catalogo" className="btn btn-primary mt-6" data-id="element-1463" data-mp="3135e096nchn2">Volver al catálogo</Link>
      </div>;
  }
  const brand = brands.find(b => b.id === vehicle.brandId);
  const title = `${brand?.name ?? ''} ${vehicle.model}`;
  const inCompare = compareIds.includes(vehicle.id);
  return <div className="container-x py-6 lg:py-10" data-id="element-1464" data-mp="kcx3gm9mvmxy">
      <Link to="/catalogo" className="inline-flex items-center gap-1.5 text-sm text-mist hover:text-white" data-id="element-1465" data-mp="pct88nq5niek">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden data-id="element-1466" data-mp="zggpj3etxgbb" /> Catálogo
      </Link>

      <div className="mt-4 grid gap-8 lg:grid-cols-[1fr_400px]" data-id="element-1467" data-mp="zikr3ep9a38f">
        <div className="min-w-0" data-id="element-1468" data-mp="uiygld0tb13b">
          <Gallery images={vehicle.images} alt={title} data-id="element-1469" data-mp="ujzqvfa07jjz" />

          <div className="mt-6 flex flex-wrap items-center gap-2" data-id="element-1470" data-mp="ppm1pulxq8nr">
            <AvailabilityBadge value={vehicle.availability} data-id="element-1471" data-mp="lpv354q62o8o" />
            <span className="text-xs text-mist" data-id="element-1472" data-mp="ln8vz676uaqz">{vehicle.segment} · {vehicle.bodyType} · Año/modelo {vehicle.year}</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl" data-id="element-1473" data-mp="bqme8qa5rzt6">{title}</h1>
          <p className="text-lg text-mist" data-id="element-1474" data-mp="6rj7n1ej5lj3">{vehicle.version}</p>

          <section className="mt-10" aria-labelledby="s-motor" data-id="element-1475" data-mp="6tvxs1ufz278">
            <h2 id="s-motor" className="font-display text-lg font-bold" data-id="element-1476" data-mp="2rsi110qpbg5">Motorización</h2>
            <dl className="mt-2 divide-y divide-line" data-id="element-1477" data-mp="21528ol7rrm95">
              <Spec label="Motor" value={vehicle.engine} data-id="element-1478" data-mp="1x4ie9pekywqp" />
              <Spec label="Transmisión" value={vehicle.transmission} data-id="element-1479" data-mp="1x5070c21astv" />
              <Spec label="Consumo" value={vehicle.consumption} data-id="element-1480" data-mp="247f72rolx7tw" />
            </dl>
          </section>

          <section className="mt-10" aria-labelledby="s-seg" data-id="element-1481" data-mp="295qgj1azpgbl">
            <h2 id="s-seg" className="mb-3 font-display text-lg font-bold" data-id="element-1482" data-mp="1v70hvh8iv8l4">Seguridad</h2>
            <ListBlock items={vehicle.safety} data-id="element-1483" data-mp="1v9cx3mmwb4uh" />
          </section>

          <section className="mt-10" aria-labelledby="s-eq" data-id="element-1484" data-mp="1z70viznsrfr4">
            <h2 id="s-eq" className="mb-3 font-display text-lg font-bold" data-id="element-1485" data-mp="1lgp28xfh7aye">Equipamiento</h2>
            <ListBlock items={vehicle.equipment} data-id="element-1486" data-mp="1q7noedn5b39k" />
          </section>

          <section className="mt-10" aria-labelledby="s-gar" data-id="element-1487" data-mp="1q9qlfhmptwns">
            <h2 id="s-gar" className="font-display text-lg font-bold" data-id="element-1488" data-mp="1bkc9c1rr914p">Garantía y disponibilidad</h2>
            <dl className="mt-2 divide-y divide-line" data-id="element-1489" data-mp="1giw0cgl7h33x">
              <Spec label="Garantía" value={vehicle.warranty} data-id="element-1490" data-mp="12il756wfbcfl" />
              <Spec label="Disponibilidad" value={vehicle.availability} data-id="element-1491" data-mp="12j4mqgylpoqg" />
              <Spec label="Vigencia de la oferta" value={quote ? `Hasta el ${formatDate(quote.ctx.offer.validUntil)}` : null} data-id="element-1492" data-mp="17g4k83hdxj5y" />
              <Spec label="Datos actualizados" value={formatDate(vehicle.updatedAt)} data-id="element-1493" data-mp="nxqifqjqbazk" />
            </dl>
          </section>

          {quote && <section className="mt-10" aria-labelledby="s-fin" data-id="element-1494" data-mp="wwu5f5m0oajc">
              <h2 id="s-fin" className="font-display text-lg font-bold" data-id="element-1495" data-mp="wtzk7bv6ilov">Condiciones financieras</h2>
              <div className="mt-2" data-id="element-1496" data-mp="sx3g76c522gx">
                <OfferStatus offer={quote.ctx.offer} source={quote.ctx.source} data-id="element-1497" data-mp="16tqcr806iqmb" />
              </div>
              <div className="mt-3" data-id="element-1498" data-mp="11w702cj4qlwh">
                {quote.ctx.circular ? <>
                    <p className="mb-3 text-sm text-mist" data-id="element-1499" data-mp="1gj9xkj7gcujt">
                      {quote.ctx.circular.circular.lender} · {quote.ctx.circular.circular.reference}. Cuotas con IVA incluido, sin seguro automotor. Gestión crediticia y gastos de otorgamiento: {quote.ctx.circular.circular.managementFeePct}% del capital financiado (promedio estimado).
                    </p>
                    <CircularTable circular={quote.ctx.circular.circular} lines={eligibleLines(quote.ctx.circular.circular, vehicle)} selected={{
                lineId: quote.ctx.circular.line.id,
                term: quote.ctx.circular.row.term
              }} data-id="element-1500" data-mp="i8qv44b2e6n5" />
                  </> : <FinanceConditions offer={quote.ctx.offer} source={quote.ctx.source} data-id="element-1501" data-mp="w1ghwayf3flb" />}
              </div>
            </section>}
        </div>

        <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start" data-id="element-1502" data-mp="15xa46m1xunga">
          <div className="card p-5" data-id="element-1503" data-mp="1ax65uen5y2so">
            <p className="text-xs text-mist" data-id="element-1504" data-mp="1ay7lyxj1dfh9">Precio de contado</p>
            <p className="font-display text-3xl font-black tracking-tight" data-id="element-1505" data-mp="1eyhpm3jcvtsy">{formatARS(vehicle.price)}</p>
            {quote?.sim && <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-line pt-4 text-sm" data-id="element-1506" data-mp="3cpkkn4ueur4v">
                <div data-id="element-1507" data-mp="3gr5u0g7qknab"><dt className="text-[11px] text-mist" data-id="element-1508" data-mp="3gr5yaserr9g4">Anticipo requerido</dt><dd className="font-semibold" data-id="element-1509" data-mp="396bquzkqp9og">{formatARS(quote.down)}</dd></div>
                <div data-id="element-1510" data-mp="398g5abg9lr2z"><dt className="text-[11px] text-mist" data-id="element-1511" data-mp="398g78gzts15m">Capital a financiar</dt><dd className="font-semibold" data-id="element-1512" data-mp="34f3k6baso2ez">{formatARS(quote.principal)}</dd></div>
                <div data-id="element-1513" data-mp="2zhttytfkzmq3"><dt className="text-[11px] text-mist" data-id="element-1514" data-mp="2zhts0nw0tcng">Cuotas estimadas</dt><dd className="font-semibold text-neon" data-id="element-1515" data-mp="2vf6vwasbh5kg">{quote.term} × {formatARS(quote.sim.firstInstallment)}</dd></div>
                <div data-id="element-1516" data-mp="3s5pphutez8la"><dt className="text-[11px] text-mist" data-id="element-1517" data-mp="3s5prg0cz5inx">TNA / CFT</dt><dd className="font-semibold" data-id="element-1518" data-mp="3s3d7jhnnoy88">{formatPct(quote.ctx.offer.tna)} / {formatPct(quote.ctx.offer.cft, 'N/I')}</dd></div>
              </dl>}
            {quote && <div className="mt-3" data-id="element-1519" data-mp="3j3823d20w9wp"><OfferStatus offer={quote.ctx.offer} source={quote.ctx.source} compact data-id="element-1520" data-mp="3j6vxyiuxx8el" /></div>}
            <div className="mt-5 grid gap-2" data-id="element-1521" data-mp="3e5eymrs3tu04">
              <Link to={`/simulador?v=${vehicle.id}`} className="btn btn-primary" data-id="element-1522" data-mp="27q5lwj3u5md2">SIMULAR FINANCIACIÓN</Link>
              <WhatsAppButton vehicle={vehicle} data-id="element-1523" data-mp="22q9s1courbb8" />
              <button onClick={() => {
              const r = toggleCompare(vehicle.id);
              if (r === 'full') toast.error('Podés comparar hasta 3 vehículos.');
            }} className="btn btn-ghost" aria-pressed={inCompare} data-id="element-1524" data-mp="22sm9zpum1loa">
                {inCompare ? <CheckIcon className="h-4 w-4" data-id="element-1525" data-mp="1o1vydpocqtr" /> : <PlusIcon className="h-4 w-4" data-id="element-1526" data-mp="1pd2glai8cok" />}
                {inCompare ? 'En el comparador' : 'Agregar al comparador'}
              </button>
            </div>
          </div>

          <div className="card p-5" data-id="element-1527" data-mp="udha3ccmyztw">
            <h2 className="font-display text-lg font-bold" data-id="element-1528" data-mp="ucfsep147f37">Consultar por esta unidad</h2>
            <p className="mb-4 mt-1 text-sm text-mist" data-id="element-1529" data-mp="fn9vj7pr20yh">Un asesor comercial te responde con disponibilidad y condiciones actualizadas.</p>
            <InquiryForm channel="vehiculo" vehicle={vehicle} allowQuote idPrefix="vd" data-id="element-1530" data-mp="kktcifdu0ru4" />
          </div>
        </aside>
      </div>
    </div>;
}